import React, {useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {PageStyleProps} from '@taopix/taopix-core/dist/types/types';
import {Checkbox, Shimmer, ThemeName, getTheme} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import Drag from '../DragAndDrop/Drag';
import {DetectionType, Lang, Localisation} from '@taopix/taopix-core';
import {DragAndDropNames} from '../../Enums';
import {useTranslation} from 'react-i18next';

type LayoutThumbnailProps = {
  pagestyle: PageStyleProps;
  thumbnailRatio: number;
  baseHeight: number;
  thumbnailPath: string;
  checked: boolean;
  onClick: (selectedLayout: PageStyleProps) => void;
  onKeyDown?: (selectedLayout: PageStyleProps, event: React.KeyboardEvent) => void;
};

const LayoutThumbnail = React.forwardRef(
  (
    {
      pagestyle,
      thumbnailRatio,
      baseHeight,
      thumbnailPath,
      checked,
      onClick,
      onKeyDown,
    }: LayoutThumbnailProps,
    ref: React.ForwardedRef<HTMLImageElement>
  ) => {
    const [loaded, setLoaded] = useState(false);
    const {ref: inViewRef, inView} = useInView({
      /* Optional options */
      threshold: 0,
    });
    const {t} = useTranslation(['toolpalette']);

    // Calculate the thumbnail's height as a percentage of its width so we can proprtionally size the box when it is empty. This is used by the tailwind before pseudo class class.
    const thumbnailWidth = baseHeight * thumbnailRatio;
    const heightPercentage = (baseHeight / thumbnailWidth) * 100;

    const thumbnailContainerClassess = classNames(
      'flex',
      'relative',
      'items-start',
      'box-border',
      // 'overflow-hidden',
      'ml-sm',
      'mt-sm',
      'before:block',
      'before:pt-[var(--height-percent)]',

      // Responsive widths for square or portrait thumbnails
      thumbnailRatio < 1.3 && [
        'max-w-[calc((100%-(var(--gap)*2))/2)]',
        'min300:max-w-[calc((100%-(var(--gap)*3))/3)]',
        'min400:max-w-[calc((100%-(var(--gap)*4))/4)]',
        'min600:max-w-[calc((100%-(var(--gap)*5))/5)]',
        'min800:max-w-[calc((100%-(var(--gap)*2))/2)]',
      ],

      // Responsive widths for landscape thumbnails (e.g. landscape single pages)
      thumbnailRatio < 1.8 && [
        'max-w-[calc((100%-(var(--gap)*2))/2)]',
        'min300:max-w-[calc((100%-(var(--gap)*3))/3)]',
        'min500:max-w-[calc((100%-(var(--gap)*4))/4)]',
        'min800:max-w-[calc((100%-(var(--gap)*2))/2)]',
      ],

      // Responsive widths for very wide (e.g. landscape spreads) landscape thumbnails
      thumbnailRatio >= 1.8 && [
        'max-w-full',
        'min300:max-w-[calc((100%-(var(--gap)*2))/2)]',
        'min500:max-w-[calc((100%-(var(--gap)*3))/3)]',
        'min800:max-w-full',
      ],

      // Get the contained style from the design system
      getTheme(ThemeName.Section, true, !checked, false),
      'rounded-[min(theme(borderRadius.themeCornerSize),5px)]',
      'overflow-hidden'
    );

    const thumbnailClassess = ['w-full', 'h-full', 'sm:w-auto'];

    return (
      <li
        key={`li-${pagestyle.ref}`}
        className={thumbnailContainerClassess}
        style={
          {
            width: thumbnailWidth + 'rem',
            '--height-percent': heightPercentage + '%', // Set the height variable for the placeholder pseudo element
          } as React.CSSProperties
        }
        data-theme={ThemeName.Section} // Allow theme tips to work on this element
        ref={inViewRef}
        onKeyDown={(event: React.KeyboardEvent) => {
          onKeyDown?.(pagestyle, event);
        }}
      >
        {!loaded && inView && <Shimmer className={'absolute top-0'} />}

        {inView && (
          <Checkbox
            label={Localisation.getLocaleString(Lang.getCode(), pagestyle.name)}
            hideLabel
            id={`pagestyle-checkbox-${pagestyle.ref}`}
            defaultChecked={checked}
            appearance={'border'}
            onChange={() => {
              if (loaded) onClick(pagestyle);
            }}
            className={'w-full h-full'}
          >
            <Drag
              name={DragAndDropNames.PAGE}
              key={`pagestyle-${pagestyle.ref}`}
              detectionType={DetectionType.PageStyle}
              item={pagestyle}
            >
              <img
                className={thumbnailClassess.join(' ')}
                src={thumbnailPath}
                alt={Localisation.getLocaleString(Lang.getCode(), pagestyle.name)}
                onLoad={() => setLoaded(true)}
                ref={ref}
              />
            </Drag>
          </Checkbox>
        )}
      </li>
    );
  }
);

export default LayoutThumbnail;
